/**
 * Step 6: Cash & Economic Assumptions
 * Captures household cash and economic assumptions for the projection
 */

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Step7FormData, Step7Schema, type AllStepFormData } from '../../types/wizard';
import { useCurrencyInput } from '../../hooks/useCurrencyInput';
import { wizardFormKeyDown } from './wizardFormKeyDown';
import { TourButton } from '../TourGuide';
import { WIZARD_STEP8_TOUR } from '../TourGuide/tourSteps';
import './Step6Assumptions.css';

interface Step6Props {
  initialData?: Partial<AllStepFormData>;
  onNext: (data: Step7FormData) => void;
  onSave: (data: Step7FormData) => void;
  onBack?: () => void;
  lastSaved?: string;
}

function Step6Assumptions({ initialData, onNext, onSave, onBack, lastSaved }: Readonly<Step6Props>) {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<Step7FormData>({
    resolver: zodResolver(Step7Schema),
    // defaultValues is the ONLY place initialData is read into the form. There
    // used to be a mount effect that called reset() with a character-for-character
    // copy of this object — it reset the form to what useForm had already set it
    // to, so it did nothing. Deleting it removed the `[]`-deps lint problem and
    // the ref latch that replaced it. Covered by wizardMountOnlyReset.test.tsx,
    // which asserts saved values reach the fields on mount.
    defaultValues: {
      householdCash: {
        jointAccountBalance: initialData?.householdCash?.jointAccountBalance || 0,
      },
      economicAssumptions: {
        equityReturnAnnualPct: initialData?.economicAssumptions?.equityReturnAnnualPct || 6.5,
        bondReturnAnnualPct: initialData?.economicAssumptions?.bondReturnAnnualPct || 3.5,
        inflationAnnualPct: initialData?.economicAssumptions?.inflationAnnualPct || 2.25,
        taxBandInflationPct: initialData?.economicAssumptions?.taxBandInflationPct ?? 0,
        statePensionGrowthAddPct: initialData?.economicAssumptions?.statePensionGrowthAddPct ?? 0.5,
        dividendYieldPct: initialData?.economicAssumptions?.dividendYieldPct ?? 2,
      },
    },
  });

  // Reset on the first run only. The ref latches whether or not data arrived,
  // which is safe because PlanWizard does not mount steps until the draft has
  // loaded (`isDraftLoading`) and remounts on planId change (`key`). If that
  // gate is ever removed, saved values would be dropped here — the latch would
  // burn on a dataless first render. Covered by wizardMountOnlyReset.test.tsx.


  const equityReturn = watch('economicAssumptions.equityReturnAnnualPct');
  const bondReturn = watch('economicAssumptions.bondReturnAnnualPct');
  const inflation = watch('economicAssumptions.inflationAnnualPct');

  // Call currency hook at top level (same pattern as Step4Property)
  const jointAccountInput = useCurrencyInput({
    value: watch('householdCash.jointAccountBalance'),
    registerProps: register('householdCash.jointAccountBalance', { valueAsNumber: true }),
  });

  const handleBlur = () => {
    // Always save on blur - isDirty doesn't work reliably with useCurrencyInput
    const data = watch();
    onSave(data);
  };

  const onSubmit = (data: Step7FormData) => {
    onNext(data);
  };

  // Check for warnings
  const showEquityBondWarning = equityReturn < bondReturn;
  const showHighInflationWarning = inflation > 10;

  return (
    <div className="step-container">
      <div className="step-header">
        <div className="step-header-row">
          <h2>Household cash and planning assumptions</h2>
          <TourButton tour={WIZARD_STEP8_TOUR} variant="text" />
        </div>
        <p>These assumptions affect your projection. You can adjust later.</p>
      </div>

      <form onSubmit={(e) => e.preventDefault()} onKeyDown={wizardFormKeyDown} className="step-form">
        {/* Section 1: Household Cash */}
        <div className="assumptions-section" data-tour="household-cash">
          <h3 className="section-header">Household Cash</h3>

          <div className="form-group">
            <label htmlFor="jointAccountBalance" className="form-label">
              {initialData?.planType === 'JOINT' ? 'Joint Account Balance' : 'Cash Savings'} (£)
            </label>
            <input
              id="jointAccountBalance"
              type="text"
              className={`form-input ${errors.householdCash?.jointAccountBalance ? 'error' : ''}`}
              {...jointAccountInput}
              onBlur={(e) => {
                jointAccountInput.onBlur(e);
                handleBlur();
              }}
            />
            {errors.householdCash?.jointAccountBalance && (
              <span className="error-message">{errors.householdCash.jointAccountBalance.message}</span>
            )}
            <span className="help-text">Combined cash savings available for retirement</span>
          </div>
        </div>

        {/* Section 2: Economic Assumptions */}
        <div className="assumptions-section">
          <h3 className="section-header">Economic Assumptions</h3>

          <p className="assumptions-disclaimer">
            The defaults below are starting points, not recommendations. Past performance
            is not a reliable indicator of future results. Adjust these to match your own
            expectations.
          </p>

          <h4 className="assumptions-subheader">Investment Returns</h4>

          <div className="form-group">
            <label htmlFor="equityReturnAnnualPct" className="form-label">
              Equity Return (% per year)
            </label>
            <div className="percentage-input-wrapper">
              <input
                id="equityReturnAnnualPct"
                type="number"
                step="0.01"
                className={`form-input percentage-input ${errors.economicAssumptions?.equityReturnAnnualPct ? 'error' : ''}`}
                {...register('economicAssumptions.equityReturnAnnualPct', { valueAsNumber: true })}
                onBlur={handleBlur}
              />
              <span className="percentage-suffix">%</span>
            </div>
            {errors.economicAssumptions?.equityReturnAnnualPct && (
              <span className="error-message">{errors.economicAssumptions.equityReturnAnnualPct.message}</span>
            )}
            {showEquityBondWarning && (
              <div className="warning-message" role="alert">
                Bond return is higher than equity return &mdash; this is unusual. Are these values correct?
              </div>
            )}
            <span className="help-text">Expected annual return on equity investments before fees</span>
            <details className="assumption-context">
              <summary>Why 6.5%?</summary>
              <p>
                Historically, annual total returns with dividends reinvested have
                been around <strong>10%</strong> for US equities (S&amp;P 500, 1926&ndash;2025),
                {' '}<strong>8%</strong> for global equities (MSCI World, 1970&ndash;2025),
                and <strong>7%</strong> for UK equities (FTSE All-Share, 1986&ndash;2025).
                These are nominal (before inflation). The default of 6.5% applies
                a modest reduction for costs and diversification. A typical range
                is <strong>4&ndash;10%</strong>.
              </p>
            </details>
          </div>

          <div className="form-group">
            <label htmlFor="bondReturnAnnualPct" className="form-label">
              Bond Return (% per year)
            </label>
            <div className="percentage-input-wrapper">
              <input
                id="bondReturnAnnualPct"
                type="number"
                step="0.01"
                className={`form-input percentage-input ${errors.economicAssumptions?.bondReturnAnnualPct ? 'error' : ''}`}
                {...register('economicAssumptions.bondReturnAnnualPct', { valueAsNumber: true })}
                onBlur={handleBlur}
              />
              <span className="percentage-suffix">%</span>
            </div>
            {errors.economicAssumptions?.bondReturnAnnualPct && (
              <span className="error-message">{errors.economicAssumptions.bondReturnAnnualPct.message}</span>
            )}
            <span className="help-text">Expected annual return on bond investments</span>
            <details className="assumption-context">
              <summary>Why 3.5%?</summary>
              <p>
                UK government gilts have historically returned around <strong>4&ndash;5%</strong> per
                year in nominal terms. Global bond indices have returned similar
                figures. Yields vary with the interest rate environment. The default
                of 3.5% reflects a long-run average across rate cycles. A typical
                range is <strong>1&ndash;5%</strong>.
              </p>
            </details>
          </div>

          <div className="form-group">
            <label htmlFor="dividendYieldPct" className="form-label">
              Dividend Yield (% per year)
            </label>
            <div className="percentage-input-wrapper">
              <input
                id="dividendYieldPct"
                type="number"
                step="0.01"
                className={`form-input percentage-input ${errors.economicAssumptions?.dividendYieldPct ? 'error' : ''}`}
                {...register('economicAssumptions.dividendYieldPct', { valueAsNumber: true })}
                onBlur={handleBlur}
              />
              <span className="percentage-suffix">%</span>
            </div>
            {errors.economicAssumptions?.dividendYieldPct && (
              <span className="error-message">{errors.economicAssumptions.dividendYieldPct.message}</span>
            )}
            <span className="help-text">Dividend income from shares held outside an ISA or pension</span>
            <details className="assumption-context">
              <summary>Why 2%?</summary>
              <p>
                The FTSE 100 has historically yielded around <strong>3.5&ndash;4%</strong> per
                year. The S&amp;P 500 yields lower, around <strong>1.5&ndash;2%</strong>, as US
                companies favour buybacks over dividends. A typical range
                is <strong>1.5&ndash;4%</strong>.
              </p>
            </details>
          </div>

          <h4 className="assumptions-subheader">Economic Environment</h4>

          <div className="form-group">
            <label htmlFor="inflationAnnualPct" className="form-label">
              Inflation Rate (% per year)
            </label>
            <div className="percentage-input-wrapper">
              <input
                id="inflationAnnualPct"
                type="number"
                step="0.01"
                className={`form-input percentage-input ${errors.economicAssumptions?.inflationAnnualPct ? 'error' : ''}`}
                {...register('economicAssumptions.inflationAnnualPct', { valueAsNumber: true })}
                onBlur={handleBlur}
              />
              <span className="percentage-suffix">%</span>
            </div>
            {errors.economicAssumptions?.inflationAnnualPct && (
              <span className="error-message">{errors.economicAssumptions.inflationAnnualPct.message}</span>
            )}
            {showHighInflationWarning && (
              <div className="warning-message" role="alert">
                This is a very high inflation assumption. Are you sure?
              </div>
            )}
            <span className="help-text">Expected annual inflation (affects spending, tax bands, and state pension)</span>
            <details className="assumption-context">
              <summary>Why 2.25%?</summary>
              <p>
                The Bank of England targets <strong>2%</strong> CPI inflation &mdash; the
                default reflects that target plus a small margin, in line with what UK
                regulators use for pension illustrations. Actual RPI inflation, a
                different and structurally higher measure, has averaged{' '}
                <strong>3.4%</strong> a year since the Bank's independence in 1997/98 and{' '}
                <strong>4.5%</strong> over the past century, including{' '}
                <strong>11.6%</strong> in 2022. Given that gap, it's worth stress-testing
                your plan at a higher rate &mdash; try <strong>2.5&ndash;4.5%</strong> to
                see how it holds up.
              </p>
            </details>
          </div>

          <div className="form-group">
            <label htmlFor="taxBandInflationPct" className="form-label">
              Tax Threshold Uprating (% of inflation)
            </label>
            <div className="percentage-input-wrapper">
              <input
                id="taxBandInflationPct"
                type="number"
                step="1"
                className={`form-input percentage-input ${errors.economicAssumptions?.taxBandInflationPct ? 'error' : ''}`}
                {...register('economicAssumptions.taxBandInflationPct', { valueAsNumber: true })}
                onBlur={handleBlur}
              />
              <span className="percentage-suffix">%</span>
            </div>
            {errors.economicAssumptions?.taxBandInflationPct && (
              <span className="error-message">{errors.economicAssumptions.taxBandInflationPct.message}</span>
            )}
            <span className="help-text">0 = thresholds stay frozen, 100 = thresholds rise with your inflation assumption</span>
            <details className="assumption-context">
              <summary>What does this mean?</summary>
              <p>
                Tax bands are currently frozen until 2031. Set to <strong>0%</strong> to
                model continued freezes (fiscal drag), or <strong>100%</strong> if you
                expect thresholds to rise with inflation after the freeze ends.
              </p>
            </details>
          </div>

          <div className="form-group">
            <label htmlFor="statePensionGrowthAddPct" className="form-label">
              State Pension Growth Above Inflation (% per year)
            </label>
            <div className="percentage-input-wrapper">
              <input
                id="statePensionGrowthAddPct"
                type="number"
                step="0.01"
                className={`form-input percentage-input ${errors.economicAssumptions?.statePensionGrowthAddPct ? 'error' : ''}`}
                {...register('economicAssumptions.statePensionGrowthAddPct', { valueAsNumber: true })}
                onBlur={handleBlur}
              />
              <span className="percentage-suffix">%</span>
            </div>
            {errors.economicAssumptions?.statePensionGrowthAddPct && (
              <span className="error-message">{errors.economicAssumptions.statePensionGrowthAddPct.message}</span>
            )}
            <span className="help-text">The triple lock typically adds 0.5&ndash;1% above inflation. Set to 0 if you expect it to end.</span>
            <details className="assumption-context">
              <summary>What does this mean?</summary>
              <p>
                Under the triple lock, the State Pension rises by the highest of inflation,
                average earnings growth, or 2.5%. This field adds extra growth above your
                inflation assumption. Historically, earnings growth has exceeded inflation
                by about <strong>0.5&ndash;1%</strong> per year.
              </p>
            </details>
          </div>
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          {onBack && (
            <button type="button" className="btn btn-secondary" onClick={onBack}>
              Back
            </button>
          )}
          <button type="button" className="btn btn-primary" onClick={() => handleSubmit(onSubmit)()}>
            Next: Withdrawal Strategy
          </button>
          {lastSaved && (
            <span className="save-indicator">
              Saved
            </span>
          )}
        </div>
      </form>
    </div>
  );
}

export default Step6Assumptions;
